import React, { FC } from "react";
import { useRouter } from "next/router";
import cx from "classnames";
import { Button } from "@pepdirect/ui/button";
import { Heading } from "@pepdirect/ui/heading";
import {
  VerifiedAddressFragment,
  VerifyAddressInput,
} from "services/graphql/generated";
import { logUnverifiedAddressSelected } from "@pepdirect/helpers/analyticsLogger";
import { errorCodes } from "constants/error";
import commonSt from "../verificationAndConfirmationModal.module.scss";
import st from "./addressVerificationModal.module.scss";

export type VerificationAddrTypes =
  | VerifiedAddressFragment
  | VerifyAddressInput;

interface AddressVerificationModalProps {
  verifiedAddress?: VerifiedAddressFragment | null;
  unverifiedAddress?: VerifyAddressInput | null;
  userConfirm?: (addr: VerificationAddrTypes) => void;
  onClose: () => void;
  loading?: boolean;
  hasInvalidZip?: boolean;
}

interface DisplayedAddressProps {
  addr: VerificationAddrTypes;
  title: string;
  hasInvalidZip?: boolean;
}

const DisplayedAddress: FC<DisplayedAddressProps> = ({
  addr,
  title,
  hasInvalidZip,
}) => (
  <div className={st.address}>
    <div className={cx(st.title, hasInvalidZip && st.invalidZip)}>{title}</div>
    <div className={st.name}>{`${addr.firstName} ${addr.lastName}`}</div>
    <div>{addr.line1}</div>
    {addr.line2 && <div>{addr.line2}</div>}
    <div>
      {`${addr.city} ${addr.state} `}
      <span className={cx(hasInvalidZip && st.invalidZip)}>{addr.zip}</span>
    </div>
  </div>
);

export const AddressVerificationModal: FC<AddressVerificationModalProps> = ({
  verifiedAddress,
  unverifiedAddress,
  userConfirm = () => {},
  onClose,
  loading,
  hasInvalidZip,
}) => {
  const { pathname } = useRouter();
  const isCheckout = !pathname.includes("dashboard");

  if (!(verifiedAddress || unverifiedAddress)) {
    // something went wrong
    return <div>{errorCodes["somethingWentWrong"]}</div>;
  }

  if (unverifiedAddress && verifiedAddress) {
    return (
      <div className={commonSt.body}>
        <div>
          <Heading level="h2" size="xl">
            {hasInvalidZip ? "Edit" : "Verify"} Shipping Address
          </Heading>

          <p>
            Our address verification system recommends the following changes
            based on the information provided by our delivery service. Please
            choose from the following:
          </p>

          <div className={st.addressesWrapper}>
            <div className={st.addresses}>
              <div>
                <DisplayedAddress
                  addr={unverifiedAddress}
                  title={hasInvalidZip ? "Invalid Zip Code" : "Entered Address"}
                  hasInvalidZip={hasInvalidZip}
                />

                {hasInvalidZip ? (
                  <Button type="cancel" onClick={onClose} fullWidth>
                    Edit Entered Address
                  </Button>
                ) : (
                  <Button
                    htmlType="submit"
                    type="tertiary"
                    loading={loading}
                    onClick={() => {
                      logUnverifiedAddressSelected();
                      userConfirm(unverifiedAddress);
                    }}
                    fullWidth
                  >
                    Keep Entered Address
                  </Button>
                )}
              </div>

              <div>
                <DisplayedAddress
                  addr={verifiedAddress}
                  title="Suggested Address"
                />

                <Button
                  htmlType="submit"
                  type="secondary"
                  loading={loading}
                  onClick={() => userConfirm(verifiedAddress)}
                  fullWidth
                >
                  Use Suggested Address
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className={st.buttons}>
          <Button type="cancel" onClick={onClose}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={commonSt.body}>
      <div>
        <Heading level="h2" size="xl">
          {hasInvalidZip ? "Edit" : "Verify"} Shipping Address
        </Heading>

        {verifiedAddress && !unverifiedAddress && (
          <>
            <p>
              Our address verification system recommends the following changes
              based on the information provided by our delivery service. Please
              confirm the suggested address below is correct
              {isCheckout ? " to complete your order" : ""}.
            </p>

            <DisplayedAddress
              addr={verifiedAddress}
              title="Suggested Address"
            />
          </>
        )}

        {unverifiedAddress && !verifiedAddress && (
          <>
            <p className={st.unverifiedText}>
              We were not able to verify the shipping address that was provided.
              {!hasInvalidZip &&
                " Do you want to proceed with the shipping address entered?"}
            </p>

            <DisplayedAddress
              addr={unverifiedAddress}
              title={hasInvalidZip ? "Invalid Zip Code" : "Entered Address"}
              hasInvalidZip={hasInvalidZip}
            />
          </>
        )}
      </div>

      <div className={st.buttons}>
        <Button type="cancel" onClick={onClose}>
          {hasInvalidZip ? "Edit Entered Address" : "Go Back"}
        </Button>

        {!hasInvalidZip && (
          <Button
            htmlType="submit"
            type="secondary"
            loading={loading}
            onClick={() => {
              if (unverifiedAddress) logUnverifiedAddressSelected();
              userConfirm(
                // one of these must exist at this point
                (verifiedAddress || unverifiedAddress) as VerificationAddrTypes
              );
            }}
          >
            Confirm
          </Button>
        )}
      </div>
    </div>
  );
};
